import React from 'react';
import { MessageSquare, Lightbulb, CheckCircle, Users, BookOpen } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const StatsBarWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  if (!state) return null;

  const { transcript, pendingContributions, requirements } = state;

  const totalContributions = pendingContributions.length;
  const acceptedContributions = pendingContributions.filter(c => c.state === 'accepted').length;
  const acceptanceRate = totalContributions > 0
    ? Math.round((acceptedContributions / totalContributions) * 100)
    : 0;
  const personaCount = requirements?.personas?.length || 0;
  const storyCount = requirements?.userStories?.length || 0;

  const stats = [
    { icon: MessageSquare, value: transcript.length, label: 'Transcripts' },
    { icon: Lightbulb, value: totalContributions, label: 'Contributions' },
    { icon: CheckCircle, value: `${acceptanceRate}%`, label: 'Acceptance' },
    { icon: Users, value: personaCount, label: 'Personas' },
    { icon: BookOpen, value: storyCount, label: 'Stories' },
  ];

  return (
    <div className="flex items-stretch h-full gap-2 px-3 py-2">
      {stats.map(({ icon: Icon, value, label }) => (
        <div
          key={label}
          className={`flex-1 flex flex-col items-center justify-center rounded-lg px-2 py-1.5 ${
            isDark
              ? 'bg-white/5 border border-white/5'
              : 'bg-slate-50 border border-slate-100'
          }`}
        >
          <Icon className={`w-4 h-4 mb-1 ${isDark ? 'text-primary/60' : 'text-primary'}`} strokeWidth={1.5} />
          <span className={`text-lg font-bold font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {value}
          </span>
          <span className={`text-xs font-medium uppercase tracking-wider ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default StatsBarWidget;
